import { getUserCountry, isCountryInRegion } from '../theme-scripts.js';

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.initializeMessages();
  }

  async initializeMessages() {
    try {
      const userCountry = await getUserCountry();
      if (!userCountry) return;

      this.filterMessagesByRegion(userCountry);
      this.initAnimation();
    } catch (error) {
      console.error('Error initializing announcement bar:', error);
    }
  }

  filterMessagesByRegion(userCountry) {
    this.querySelectorAll('.text-animation-slider__item').forEach((element) => {
      const region = element.dataset.countries;
      const shouldShow = region === 'global' || this.isMessageValidForRegion(region, userCountry);

      if (!shouldShow) {
        element.remove();
      }
    });
  }

  isMessageValidForRegion(region, userCountry) {
    const regions = window.storeSelectorConfig?.regions;
    if (!regions) return false;

    switch (region) {
      case 'global':
        return true;
      case 'unsupported':
        return !Object.values(regions).some((r) => r.countryCodes.includes(userCountry));
      default:
        return isCountryInRegion(userCountry, region);
    }
  }

  initAnimation() {
    this.animateContainer = this.querySelector('.text-animation-slider__container');
    if (!this.animateContainer) return;

    this.animateSlider = this.querySelector('.text-animation-slider__slider');
    this.animatedItems = this.animateSlider.querySelectorAll('.text-animation-slider__item');

    if (!this.animatedItems.length) return;

    if (this.animatedItems.length === 1) {
      const item = this.animatedItems[0];
      item.style.transition = 'none';
      item.style.transform = 'translate3d(0, 0, 0)';
      item.classList.add('active');
      return;
    }

    this.animatedItems[0].classList.add('active', 'animated');

    this.animateTextTimeout = null;
    this.animateTextTimeoutResize = null;
    this.firstLoad = true;
    this.autoRotate = this.dataset.autoRotate !== 'false';
    this.animateDuration = parseInt(this.dataset.speed || this.animateSlider.dataset.delay, 10);

        this.paused = false;
    this.animateDuration = parseInt(this.animateSlider.dataset.delay, 10);

    if (this.animatedItems.length > 1) {
      this.addEventListener('mouseenter', () => this.pauseAnimation());
      this.addEventListener('mouseleave', () => this.resumeAnimation());
      this.addEventListener('focusin', () => this.pauseAnimation());
      this.addEventListener('focusout', () => this.resumeAnimation());
      this.animate();
    }

    // Arrow navigation
    this.prevBtn = this.querySelector('[data-announcement-prev]');
    this.nextBtn = this.querySelector('[data-announcement-next]');

    if (this.animatedItems.length > 1) {
      // Show arrows when there are 2+ messages
      if (this.prevBtn) this.prevBtn.classList.remove('tw-hidden');
      if (this.nextBtn) this.nextBtn.classList.remove('tw-hidden');

      // Bind arrow clicks
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.stopAutoRotate();
          this.goToPrev();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.stopAutoRotate();
          this.goToNext();
        });
      }

      // Start auto-rotate if enabled
      if (this.autoRotate) {
        this.animate();
      }
    }
  }

  goToNext() {
    const activeElement = this.animateSlider.querySelector('.active');
    if (!activeElement) return;

    const nextElement = activeElement.nextElementSibling || this.animateSlider.firstElementChild;
    this.transitionTo(activeElement, nextElement);
  }

  goToPrev() {
    const activeElement = this.animateSlider.querySelector('.active');
    if (!activeElement) return;

    const prevElement = activeElement.previousElementSibling || this.animateSlider.lastElementChild;
    this.transitionTo(activeElement, prevElement, true);
  }

  transitionTo(from, to, reverse = false) {
    const classes = {
      noAnimation: 'animated',
      animateIn: 'animate-in',
      animateOut: 'animate-out',
      active: 'active',
    };

    from.classList.add(classes.animateOut);
    from.classList.remove(classes.active, classes.noAnimation, classes.animateIn);

    to.classList.add(classes.active, classes.animateIn);
  }

  stopAutoRotate() {
    if (this.animateTextTimeout) {
      clearTimeout(this.animateTextTimeout);
      this.animateTextTimeout = null;
    }
  }

  pauseAnimation() {
    this.paused = true;
    if (this.animateTextTimeout) {
      clearTimeout(this.animateTextTimeout);
      this.animateTextTimeout = null;
    }
  }

  resumeAnimation() {
    if (!this.paused) return;
    this.paused = false;
    this.animate();
  }

  animate() {
    this.animateTextTimeout = setTimeout(() => {
      this.goToNext();
      if (this.paused) return;

      const activeElement = this.animateSlider.querySelector('.active');
      if (!activeElement) return;

      const classes = {
        noAnimation: 'animated',
        animateIn: 'animate-in',
        animateOut: 'animate-out',
        active: 'active',
      };

      const nextElement = activeElement.nextElementSibling || this.animateSlider.firstElementChild;

      activeElement.classList.add(classes.animateOut);
      activeElement.classList.remove(classes.active, classes.noAnimation, classes.animateIn);

      nextElement.classList.add(classes.active, classes.animateIn);

      this.firstLoad = false;
      this.animate();
    }, this.animateDuration);
  }

  disconnectedCallback() {
    if (this.animateTextTimeout) {
      clearTimeout(this.animateTextTimeout);
    }
    if (this.animateTextTimeoutResize) {
      clearTimeout(this.animateTextTimeoutResize);
    }
  }
}

customElements.define('announcement-bar', AnnouncementBar);
